import type { Tool } from '@/features/traceability/type/Tool';

export type ReferenceMetricsAggregation = {
  reference: string;
  meanTotalDuration: number;
  meanPieceNumber: number;
  meanVolumeRemoved: number;
  meanMalround: number;
  toolCount: number;
};

/**
 * Aggregates tool metrics by reference, calculating mean values across all tools in each reference
 */
export function aggregateToolMetricsByReference(
  tools: Tool[],
  references: string[]
): ReferenceMetricsAggregation[] {
  return references
    .map((reference) => {
      // Filter tools for this reference
      const referenceTools = tools.filter(
        (tool) => tool.reference && tool.reference.trim() === reference
      );

      if (referenceTools.length === 0) {
        return null;
      }

      // Aggregate metrics for each tool
      const toolMetrics = referenceTools.map((tool) => {
        const traces = tool.traces || [];

        // Calculate totals for this tool
        let totalDuration = 0;
        let totalPieces = 0;
        let totalVolume = 0;
        let malroundSum = 0;
        let malroundCount = 0;

        traces.forEach((trace) => {
          if (trace.totalMachiningDuration != null) {
            totalDuration += trace.totalMachiningDuration;
          }
          if (trace.machinedPieces != null) {
            totalPieces += trace.machinedPieces;
          }
          if (trace.machinedVolume != null) {
            totalVolume += trace.machinedVolume;
          }
          if (trace.malround != null) {
            malroundSum += trace.malround;
            malroundCount++;
          }
        });

        return {
          totalDuration,
          totalPieces,
          totalVolume,
          malround: malroundCount > 0 ? malroundSum / malroundCount : 0,
        };
      });

      const toolCount = toolMetrics.length;

      // Calculate means across all tools in this reference
      const meanTotalDuration =
        toolMetrics.reduce((sum, m) => sum + m.totalDuration, 0) / toolCount;
      const meanPieceNumber = toolMetrics.reduce((sum, m) => sum + m.totalPieces, 0) / toolCount;
      const meanVolumeRemoved = toolMetrics.reduce((sum, m) => sum + m.totalVolume, 0) / toolCount;
      const meanMalround = toolMetrics.reduce((sum, m) => sum + m.malround, 0) / toolCount;

      return {
        reference,
        meanTotalDuration,
        meanPieceNumber,
        meanVolumeRemoved,
        meanMalround,
        toolCount,
      };
    })
    .filter((item): item is ReferenceMetricsAggregation => item !== null);
}
